import React, {
  ReactNode,
  RefObject,
  TextareaHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import { useRows } from '~/components/chat';
import { RenderFeatureFlag } from '~/components/render-feature-flag/RenderFeatureFlag';
import { appPaths } from '~/routes/app-paths';
import { Statistics } from '~/views/alfred/document-search/document-sources-view/document-sources-table/use-document-sources-table-store/useDocumentSourcesTableStore';
import { useDocumentSearch } from '~/views/alfred/document-search/use-document-search/useDocumentSearch';
import { useUserUpload } from '~/views/alfred/research/use-user-upload/useUserUpload';
import {
  ChatMode,
  useChatNavigation,
} from '~/views/alfred/use-chat-navigation/useChatNavigation';

import { AlfredTools } from '../alfred-tools/AlfredTools';
import { ChatInputContextHeader } from '../chat-input-context-header/ChatInputContextHeader';
import { UploadFileStatusDropdown } from '../upload-file-status-dropdown/UploadFileStatusDropdown';
import styles from './ChatDocumentContextInput.module.scss';
import { checkChatInputVisibility } from './utils/checkChatInputVisibility';

export interface ChatDocumentContextInputProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onKeyDown'> {
  children?: ReactNode;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  isMessagePending: boolean;
  contextCounts?: {
    documentsCount: number;
    subTypeCount: number;
    statistics?: Statistics;
  };
  allowedModes: ChatMode[];
  onKeyDownProp?: React.KeyboardEventHandler<HTMLTextAreaElement>;
}

export const ChatDocumentContextInput = ({
  children,
  value,
  isMessagePending,
  inputRef,
  contextCounts,
  allowedModes,
  autoFocus,
  placeholder,
  disabled,
  onChange,
  onKeyDownProp,
}: ChatDocumentContextInputProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const uploadDropdownRef = useRef<HTMLDivElement | null>(null);
  const analysisModeDropdownRef = useRef<HTMLDivElement | null>(null);
  const webSearchDropdownRef = useRef<HTMLDivElement | null>(null);

  const { rows, updateRows } = useRows({
    value: value,
    textAreaRef: inputRef,
  });

  const { mode, previousMode } = useChatNavigation();
  const location = useLocation();
  const isAlfredPage = location.pathname === appPaths.alfred;
  const hasActiveDocumentMessage = useDocumentSearch(
    state => !state.isMessagePending && !!state.messages.length
  );
  const { files } = useUserUpload(state => ({
    files: state.files,
  }));

  const [showTools, setShowTools] = useState(isAlfredPage);

  useEffect(() => {
    const handleDocumentClick = (e: globalThis.MouseEvent) => {
      if (isAlfredPage) {
        return;
      }

      const target = e.target;

      if (!(target instanceof HTMLElement)) {
        return;
      }

      const withinTarget = wrapperRef.current?.contains(target);
      const isInsideChatDropdown =
        uploadDropdownRef.current?.contains(target) ||
        analysisModeDropdownRef.current?.contains(target) ||
        webSearchDropdownRef.current?.contains(target);

      if (withinTarget || isInsideChatDropdown) {
        return;
      }

      setShowTools(false);
    };

    document.addEventListener('mousedown', handleDocumentClick);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [isAlfredPage]);

  useEffect(() => {
    if (isAlfredPage) {
      setShowTools(true);
    } else {
      setShowTools(previousMode !== null);
    }
  }, [isAlfredPage, previousMode]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateRows(event);
    onChange?.(event);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = e => {
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.stopPropagation();
    } else {
      onKeyDownProp?.(e);
    }
  };

  const {
    hasValidFiles,
    showToolsOnNonAlfredPage,
    showToolsOnAlfredPage,
    showChildrenInTextArea,
    showChildrenInTools,
  } = checkChatInputVisibility({
    files,
    isAlfredPage,
    mode,
    showTools,
  });

  return (
    <div
      className={
        isMessagePending
          ? styles.chatInputFieldWrapperPending
          : styles.chatInputFieldWrapper
      }
    >
      <div className={styles.chatInputFieldContainer} ref={wrapperRef}>
        {mode === 'document' && isAlfredPage && hasActiveDocumentMessage && (
          <ChatInputContextHeader contextCounts={contextCounts} />
        )}
        <RenderFeatureFlag requiredFlag={'alfred_user_upload'}>
          {mode === 'research' && (
            <div
              className={
                hasValidFiles
                  ? styles.uploadingSection
                  : styles.uploadingSectionNoPadding
              }
            >
              <UploadFileStatusDropdown />
            </div>
          )}
        </RenderFeatureFlag>
        <div className={styles.textAreaWrapper} data-chat-textarea-wrapper>
          <textarea
            id="dropdown-trigger"
            className={styles.textareaElement}
            ref={inputRef}
            onChange={handleChange}
            rows={rows}
            value={value}
            autoFocus={autoFocus}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={() => {
              setShowTools(true);
            }}
            data-testid="chat-input"
            data-chat-textarea-element
            {...(mode === 'research'
              ? { onKeyDown }
              : { onKeyDown: onKeyDownProp })}
          />
          {showChildrenInTextArea && children}
        </div>
        {(showToolsOnNonAlfredPage || showToolsOnAlfredPage) && (
          <AlfredTools
            isAlfredPage={isAlfredPage}
            allowedModes={allowedModes}
            mode={mode}
            isMessagePending={isMessagePending}
            showChildrenInTools={showChildrenInTools}
            uploadDropdownRef={uploadDropdownRef}
            analysisModeDropdownRef={analysisModeDropdownRef}
            webSearchDropdownRef={webSearchDropdownRef}
          >
            {children}
          </AlfredTools>
        )}
      </div>
    </div>
  );
};
